/**
 * Affiliate Dashboard View
 * Displays referral link, stats, and earnings history
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Link2,
  Copy,
  Check,
  Euro,
  Clock,
  CheckCircle,
  Loader2,
  Gift,
  TrendingUp,
  ArrowLeft,
  ExternalLink,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// ============================================================================
// Types
// ============================================================================

interface AffiliateStats {
  total_referrals: number;
  total_earnings_cents: number;
  pending_earnings_cents: number;
  paid_earnings_cents: number;
}

interface AffiliateEarning {
  id: string;
  amount_in_cents: number;
  status: 'pending' | 'paid' | 'cancelled';
  created_at: string;
  source_user_email?: string;
}

interface AffiliateDashboardViewProps {
  onBack?: () => void;
  /** Navigate to Settings → Partnerkonto tab to enter payout bank details */
  onNavigateToPayoutSettings?: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export const AffiliateDashboardView: React.FC<AffiliateDashboardViewProps> = ({ onBack, onNavigateToPayoutSettings }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [earnings, setEarnings] = useState<AffiliateEarning[]>([]);
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Generate referral link using short code
  const referralLink = referralCode ? `${window.location.origin}?ref=${referralCode}` : '';

  // ============================================================================
  // Load Data
  // ============================================================================

  useEffect(() => {
    const loadAffiliateData = async () => {
      if (!user) return;

      setIsLoading(true);
      try {
        // Fetch user's referral code from profile
        const { data: profileData } = await supabase
          .from('profiles')
          .select('referral_code')
          .eq('id', user.id)
          .single();

        if (profileData?.referral_code) {
          setReferralCode(profileData.referral_code);
        }

        // Fetch stats using RPC function
        const { data: statsData, error: statsError } = await supabase
          .rpc('get_affiliate_stats');

        if (statsError) {
          console.error('Error fetching affiliate stats:', statsError);
        } else if (statsData && statsData.length > 0) {
          setStats(statsData[0]);
        }

        // Fetch recent earnings
        const { data: earningsData, error: earningsError } = await supabase
          .from('affiliate_earnings')
          .select('id, amount_in_cents, status, created_at')
          .eq('affiliate_user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (earningsError) {
          console.error('Error fetching earnings:', earningsError);
        } else {
          setEarnings(earningsData || []);
        }
      } catch (error) {
        console.error('Error loading affiliate data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAffiliateData();
  }, [user]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [referralLink]);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/10 border-emerald-500/30 gap-1">
            <CheckCircle className="w-3 h-3" /> Ausgezahlt
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 hover:bg-amber-500/10 border-amber-500/30 gap-1">
            <Clock className="w-3 h-3" /> Ausstehend
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="outline" className="bg-destructive/10 text-destructive hover:bg-destructive/10 border-destructive/30 gap-1">
            <XCircle className="w-3 h-3" /> Storniert
          </Badge>
        );
      default:
        return null;
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-start gap-4">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Gift className="w-8 h-8 text-primary" />
            Empfehlen &amp; Verdienen
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Erhalte <span className="font-semibold text-primary">20% Lifetime-Provision</span> auf 
            alle Umsätze deiner geworbenen Nutzer. Du verdienst bei jedem Credit-Kauf mit – ein Leben lang!
          </p>
        </div>
      </div>

      {/* Referral Link Generator */}
      <Card className="bg-gradient-to-r from-primary to-purple-600 border-none text-primary-foreground shadow-xl">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Dein persönlicher Empfehlungslink
          </h2>
          <p className="text-primary-foreground/80 text-sm mb-4">
            Teile diesen Link mit Kollegen und Freunden. Sobald sich jemand über deinen Link registriert und Credits kauft, 
            erhältst du automatisch 20% Provision.
          </p>
          
          <div className="flex gap-2">
            <div className="flex-1 bg-white/20 backdrop-blur-sm rounded-md px-4 py-2 font-mono text-sm flex items-center">
              <span className="truncate">{referralLink}</span>
            </div>
            <Button
              onClick={handleCopyLink}
              variant={copied ? "default" : "secondary"}
              className={copied ? "bg-emerald-500 hover:bg-emerald-600 text-white border-none" : "bg-white text-primary hover:bg-white/90 border-none"}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Kopiert!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Kopieren
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Referrals */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Geworbene Nutzer</p>
                <p className="text-3xl font-bold mt-1">
                  {stats?.total_referrals || 0}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-xl">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Nutzer, die sich über deinen Link registriert haben
            </p>
          </CardContent>
        </Card>

        {/* Pending Earnings */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Offene Provision</p>
                <p className="text-3xl font-bold text-amber-500 mt-1">
                  {formatCurrency(stats?.pending_earnings_cents || 0)}
                </p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-xl">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Wird bei Erreichen von 50€ ausgezahlt
            </p>
          </CardContent>
        </Card>

        {/* Paid Earnings */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Bereits ausgezahlt</p>
                <p className="text-3xl font-bold text-emerald-500 mt-1">
                  {formatCurrency(stats?.paid_earnings_cents || 0)}
                </p>
              </div>
              <div className="p-3 bg-emerald-500/10 rounded-xl">
                <Euro className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Gesamtsumme aller bisherigen Auszahlungen
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Total Earnings Highlight */}
      {stats && stats.total_earnings_cents > 0 && (
        <Alert className="bg-muted border-border">
          <TrendingUp className="h-4 w-4" />
          <AlertTitle>Gesamt-Provision (Lifetime)</AlertTitle>
          <AlertDescription className="text-2xl font-bold mt-1 text-foreground">
             {formatCurrency(stats.total_earnings_cents)}
          </AlertDescription>
        </Alert>
      )}

      {/* Earnings History */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Euro className="w-5 h-5 text-muted-foreground" />
            Provisionshistorie
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {earnings.length === 0 ? (
            <div className="p-8 text-center">
              <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Noch keine Provisionen</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Teile deinen Link und verdiene bei jedem Kauf mit!
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {earnings.map((earning) => (
                <div
                  key={earning.id}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <p className="font-medium">
                      Provision aus Credit-Kauf
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(earning.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="text-right space-y-1">
                    <p className={`font-semibold ${
                      earning.status === 'cancelled' ? 'text-muted-foreground line-through' : 'text-emerald-600'
                    }`}>
                      +{formatCurrency(earning.amount_in_cents)}
                    </p>
                    {getStatusBadge(earning.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <h3 className="font-bold text-primary mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5" />
            So funktioniert's
          </h3>
          <ul className="space-y-2 text-sm text-primary/90">
            <li className="flex items-start gap-2">
              <span className="font-semibold text-primary">1.</span>
              Teile deinen persönlichen Link mit Kollegen, Freunden oder in deinem Netzwerk.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-primary">2.</span>
              Wenn sich jemand über deinen Link registriert und Credits kauft, erhältst du 20% des Kaufpreises.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-primary">3.</span>
              Die Provision gilt lebenslang – bei jedem weiteren Kauf deiner geworbenen Nutzer.
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-primary">4.</span>
              Ab 50€ Guthaben erfolgt die Auszahlung auf dein hinterlegtes Bankkonto.
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/20">
            <Button
              variant="link"
              className="text-primary hover:text-primary/80 p-0 h-auto font-medium"
              onClick={onNavigateToPayoutSettings}
              disabled={!onNavigateToPayoutSettings}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Bankdaten für Auszahlungen hinterlegen →
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
